<template>
  <form>
    <div class="image-container mb-3" v-if="previewPath">
      <img :src="previewPath" alt="Uploaded Image Preview" />
    </div>
    <div class="form-group">
      <div ref="dashboardContainer"></div>
    </div>
    <!-- FT-152: отказ по ограничению перестаёт быть немым. Информер Uppy живёт 5 секунд,
         а restriction-ошибки библиотека и вовсе глотает — сотрудник узнавал об отброшенных
         файлах только по их отсутствию в брони. -->
    <v-alert
      v-if="rejectedFiles.length"
      dense
      text
      type="warning"
      class="mt-2 mb-0"
    >
      {{ $t("bookings.photos_rejected") }}: {{ rejectedFiles.join(", ") }}
    </v-alert>
  </form>
</template>

<script>
import Uppy from "@uppy/core";

import Dashboard from "@uppy/dashboard";
import Russian from "@uppy/locales/lib/ru_RU";
import English from "@uppy/locales/lib/en_US";
import { guardMultipartAbort, BatchTolerantAwsS3Multipart } from "@/utils/uppyS3Multipart";
import { confirmWithRetries, createLimiter, CONFIRM_LIMIT } from "@/utils/confirmRetry";
// import XHRUpload from "@uppy/xhr-upload";
import Compressor from "@uppy/compressor";
// import Webcam from '@uppy/webcam';
import "@uppy/core/dist/style.css";
import "@uppy/dashboard/dist/style.css";
import '@uppy/webcam/dist/style.min.css';
import { RASTER_IMAGE_TYPES, VIDEO_TYPES } from "@/utils/uploadFileTypes";

// FT-152 REQ-03: лимит считается ЗА ОТПРАВКУ. Число одно, но подставляется в двух
// местах (стартовые restrictions и пересчёт потолка), поэтому вынесено в константу.
// 30, а не 15 (решение USER 2026-09-12). Байты идут мимо нашего сервера: браузер
// пишет прямо в S3, а промоушен внутри Shrine — серверный copy_object в самом S3
// (shrine/storage/s3.rb#copy), деривативы на промоушене не строятся
// (`create_on_promote: false`), то есть ImageMagick на пачку не запускается.
// Параллельность при этом НЕ растёт: сжатие ограничено пятью файлами
// (`limit: 5` у @uppy/compressor ниже), отправка в S3 — TRANSPORT_LIMIT, поэтому
// пачка из 30 просто идёт дольше, а не грузит сеть телефона сильнее.
export const BATCH_LIMIT = 30;

// FT-153 REQ-03: потолок зависит от типа.
//
// Uppy проверяет размер в addFiles, то есть ДО компрессора, а компрессор здесь настроен
// на limit: 5 — пачка тяжёлых кадров распаковывается пятёрками и может уронить вкладку
// по памяти. Поэтому глобальный потолок ставим по видео, а не-видео режем своим хуком.
// Видео мимо компрессора идёт потоком, ему 300 МБ безопасны.
export const UPLOAD_LIMIT = 300 * 1024 * 1024;
export const PHOTO_LIMIT = 50 * 1024 * 1024;

// Сколько частей уезжает в S3 одновременно.
//
// ЭТО НЕ ТО ЖЕ, что CONFIRM_LIMIT, хотя первая редакция утверждала обратное и ставила
// сюда тройку со ссылкой на двенадцать слотов Puma. Обоснование было неверным, и это
// показал замер на стенде: части идут PUT'ом НАПРЯМУЮ в Yandex S3 и наших слотов не
// трогают вовсе. В api на файл приходит около шести коротких запросов — create,
// несколько пакетных подписей и complete, — и их число от параллельности не зависит.
// Значит аргумент про слоты ограничивает подтверждения, а не транспорт.
//
// Пять — дефолт Uppy. Замер прогона 14.09: 57 МБ ушли за 150 секунд тремя потоками,
// то есть ~390 КБ/с суммарно и ~127 КБ/с на поток. Упирается это в канал или в
// задержку — из одного прогона не видно; пять потоков помогут во втором случае и не
// навредят в первом.
export const TRANSPORT_LIMIT = 5;

// Размер части. Uppy по умолчанию берёт max(5 МБ, size/10000), то есть на 300 МБ даёт
// шестьдесят частей и больше двадцати round-trip'ов за подписями. Целимся в двадцать
// частей, но не мельче библиотечного минимума — на файлах до 100 МБ это ничего не
// меняет (там побеждает 5 МБ), а на 300 МБ даёт двадцать частей вместо шестидесяти.
//
// Цена названа: оборванная часть теперь стоит до 15 МБ вместо 5. Это по-прежнему
// несопоставимо с перезаливкой всего файла, ради которой фича и делается.
export const TARGET_PART_COUNT = 20;

export function partSizeFor(file) {
  return Math.ceil((file && file.size ? file.size : 0) / TARGET_PART_COUNT);
}

// Ровно .mov и ничего больше: штатный allowedFileTypes разрешает по имени файла только
// его (VIDEO_TYPES = ["video/*", ".mov"]). Более широкий список пропускал бы .mp4 с
// generic MIME мимо хука, а restricter всё равно отбивал бы его — расхождение без выгоды.
const VIDEO_EXTENSION = /\.mov$/i;

export default {
  props: {
    width: {
      type: Number,
      required: true,
    },
    height: {
      type: Number,
      required: true,
    },
    booking_id: {
      type: Number,
      required: true,
    },
    theme: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      payload: null,
      previewPath: null,
      disabled: true,
      rejectedFiles: [],
      // Ссылки на превью, созданные НАМИ: Uppy отзывает только свои.
      ownedPreviews: [],
      // id упавших файлов, оставшихся лежать в состоянии после прошлой отправки.
      // Именно id, а не счётчик: когда сверху добавлена новая пачка, по одному лишь
      // количеству файлов в состоянии старые от новых не отличить, и потолок дрейфует.
      carryOverIds: {},
      rejectionRoundOpen: false,
    };
  },
  mounted() {
    this.instantiateUppy();
    console.log(process.env.NODE_ENV)
  },
  beforeDestroy() {
    // Ушли со страницы — прекращаем повторы подтверждения. Без этого они продолжают
    // добивать сервер после уничтожения компонента.
    if (this.confirmAbort) this.confirmAbort.abort();
    this.uppy.close();
    // Свои превью отзываем только здесь: раньше нельзя — галерея показывает их до
    // ближайшего обновления списка с сервера.
    this.ownedPreviews.forEach((url) => URL.revokeObjectURL(url));
    this.ownedPreviews = [];
  },
  methods: {
    instantiateUppy() {
      let self = this;
      this.uppy = new Uppy({
        debug: false,
        locale: this.$i18n.locale === "ru" ? Russian : English,
        autoProceed: true,
        allowMultipleUploads: true,
        // Возврат false эмитит restriction-failed и бросает RestrictionError, который
        // addFiles глотает наравне с прочими ограничениями — файл попадёт в существующий
        // список отклонённых и будет назван по имени, без второй поверхности.
        onBeforeFileAdded: (file) => {
          const isVideo = (file.type || "").startsWith("video/")
            || VIDEO_EXTENSION.test(file.name || "");

          if (!isVideo && file.size > PHOTO_LIMIT) return false;

          // Тип чиним ЗДЕСЬ, в одном месте. Браузер отдаёт для .mov
          // application/octet-stream, мы такой файл впускаем по расширению — и дальше
          // этот же тип уезжает в подтверждение, ложится в image_data и попадает в
          // галерею, а Gallary.vue решает «видео ли это» по content_type. Без подстановки
          // файл, который мы специально впустили, рисуется как <img> без иконки play.
          if (isVideo && !(file.type || "").startsWith("video/")) {
            return { ...file, type: "video/quicktime" };
          }

          return true;
        },
        restrictions: {
          maxFileSize: UPLOAD_LIMIT,
          minNumberOfFiles: 1,
          maxNumberOfFiles: BATCH_LIMIT,
          allowedFileTypes: [...RASTER_IMAGE_TYPES, ...VIDEO_TYPES],
        }
      })
      // Фото повреждений при выдаче и приёме — это доказательство в споре с
      // клиентом, а не иллюстрация. Отсюда все три отличия от прежних настроек.
      //
      // Рамка. width/height при resize: "none" вписывают кадр в прямоугольник
      // 1920×1080, и для вертикального снимка решает высота: 3024×4032 садится
      // в 810×1080. Царапина — тонкая линия с высоким контрастом, то есть ровно
      // то, что первым съедает даунсемплинг. maxWidth/maxHeight задают то, что
      // и имелось в виду, — длинную сторону 1920, не трогая пропорции: тот же
      // снимок остаётся 1440×1920, втрое больше пикселей.
      //
      // quality 0.8 вместо 0.7 по той же причине: артефакты JPEG садятся на
      // контуры. Выше не берём — здесь грузят пачкой до BATCH_LIMIT штук с телефона
      // по мобильной сети, и вес кадра тут не абстракция.
      //
      // mimeType. Без него compressorjs держит тип входа (mimeType: "auto"), и
      // HEIC с айфона уезжал в canvas.toBlob("image/heic") — такой кодировки у
      // браузера нет, он молча падал в PNG. То есть фотография сохранялась
      // PNG'ом на 1920 по длинной стороне, весом в разы больше JPEG, и это на
      // самом массовом здесь пути. Явный "image/jpeg" убирает и это, и
      // невозможность открыть HEIC в галерее из Chrome. convertTypes и
      // convertSize при явном mimeType недостижимы, поэтому их больше нет.
      //
      // mimeTypes (мн. ч.) и preserveExif убраны как мёртвые: их не читает ни
      // @uppy/compressor, ни compressorjs (там mimeType и retainExif).
      // Видео и .mov компрессор не трогает — гейт file.type.startsWith("image/").
      .use(Compressor, {
        quality: 0.8,
        limit: 5,
        mimeType: "image/jpeg",
        maxWidth: 1920,
        maxHeight: 1920,
        locale: {
          strings: {
            // Shown in the Status Bar
            compressingImages: this.$i18n.locale === "ru" ? 'Сжимаем изображение...' : 'Compressing image...',
            compressedX: this.$i18n.locale === "ru" ? 'Сжато %{size} байт' : 'Compressed %{size} bytes',
          },
        },
      })
      .use(Dashboard, {
        hideUploadButton: true,
        theme: this.theme,
        inline: true,
        width: this.width,
        height: this.height,
        target: this.$refs.dashboardContainer,
        replaceTargetContent: true,
        showProgressDetails: true,
        browserBackButtonClose: true,
        locale: {
          strings: {
            poweredBy2: "",
          },
        }
      })
      .use(BatchTolerantAwsS3Multipart, {
        // FT-153 REQ-01: загрузка частями. Обрыв стоит одной части, а не всего файла —
        // ради этого фича и делается. companionUrl указывает на уже смонтированный
        // UPPY_S3_CHECKIN_PHOTOS (routes.rb, namespace :checkin_bookings).
        limit: TRANSPORT_LIMIT,
        getChunkSize: partSizeFor,
        companionUrl: `${this.$store.getters.getApiUrl}/checkin_bookings/`,
      });

      // Защита отмены: при отмене, пришедшей пока createMultipartUpload в полёте,
      // библиотека теряет key/uploadId и шлёт DELETE .../null?key=null, оставляя загрузку
      // висеть на S3 (прод-инцидент 23/26.08, разбор в самом модуле). Аппаратная кнопка
      // «назад» размонтирует компонент — для приёма у машины это штатное событие.
      guardMultipartAbort(this.uppy);

      // Ограничитель подтверждений — ОДИН на компонент, а не на пачку: при
      // allowMultipleUploads вторая пачка входит в постпроцессор, пока первая ещё
      // подтверждается, и пул на вызов дал бы шесть одновременных запросов при
      // обещанных трёх. Слотов на весь прод двенадцать.
      this.confirmLimiter = createLimiter(CONFIRM_LIMIT);
      // .use(XHRUpload, {
      //   endpoint: process.env.NODE_ENV === "development" ?
      //     `http://localhost:3000/api/v1/booking_photos/${this.booking_id}/` :
      //     `https://rentprog.net/api/v1/booking_photos/${this.booking_id}/`,
      //   method: "post",
      //   formData: true,
      //   fieldName: "images[]",
      //   bundle: false,
      //   headers: {
      //     Authorization: this.$store.getters.getAuthToken,
      //   },
      // });

      // FT-152 DEC-05: повтор не пережимает уже сжатый файл.
      //
      // uppy.retryUpload прогоняет ПРЕпроцессоры заново, а компрессор гейтится только
      // типом файла — значит каждый повтор давил бы уже сжатый JPEG качеством 0.8,
      // съедая ровно те детали (тонкая царапина), ради которых кадр и снят.
      // Своего маркера у @uppy/compressor 1.1.2 нет (в мету он пишет только type и name),
      // а хука для гейта не предусмотрено — поэтому снимаем его препроцессор и ставим
      // свою обёртку. Ссылка та же: плагин биндит prepareUpload в конструкторе.
      const compressor = this.uppy.getPlugin('Compressor');
      if (compressor) {
        this.uppy.removePreProcessor(compressor.prepareUpload);
        this.uppy.addPreProcessor(async (fileIDs) => {
          const fresh = fileIDs.filter((id) => {
            const f = this.uppy.getFile(id);
            return f && !f.meta.ft152Compressed;
          });
          if (!fresh.length) return;

          const sizeBefore = {};
          fresh.forEach((id) => {
            const f = this.uppy.getFile(id);
            if (f) sizeBefore[id] = f.size;
          });

          await compressor.prepareUpload(fresh);

          // Признак ставим только тем, кого компрессор ДЕЙСТВИТЕЛЬНО пережал. Он ловит
          // отказ по каждому файлу сам и резолвится успешно, оставляя часть пачки
          // несжатой; пометить такой файл — значит навсегда лишить его сжатия.
          fresh.forEach((id) => {
            const f = this.uppy.getFile(id);
            if (f && f.size !== sizeBefore[id]) {
              this.uppy.setFileMeta(id, { ft152Compressed: true });
            }
          });
        });
      }

      this.uppy.on('error', (error) => {
        console.error('Error file upload:', error);
      });

      this.uppy.on('upload-error', (file, error, response) => {
        console.error('Upload error:', {
          file: file.name,
          error: error.message || error,
          response: response || 'No response',
          meta: file.meta || 'No metadata'
        });
      });

      // FT-152: карты и потолок живут ровно столько, сколько сам файл.
      //
      // id файла в Uppy детерминирован — имя, тип, размер, lastModified
      // (@uppy/utils/lib/generateFileID.js), — а доставленные файлы мы снимаем из
      // состояния. Значит сотрудник может выбрать ТУ ЖЕ фотографию снова и получить
      // ТОТ ЖЕ id. Пока номер строки жил во внешней карте компонента, он переживал
      // снятие файла, и повторный выбор той же фотографии уходил с booking_photo_id уже
      // заполненной строки. FT-153 карты убрал: номер живёт в мете самого файла и
      // умирает вместе с ним, поэтому инвариант держится сам. Потолок пачки по-прежнему
      // требует уборки здесь. Прежде дубли закрывал noDuplicates — снятие файлов его
      // обошло.
      this.uppy.on('file-removed', (file) => {
        // Упавший файл убрали руками — место освободилось, и потолок обязан опуститься.
        // Иначе он дрейфует вверх и следующая пачка примет больше, чем BATCH_LIMIT.
        delete this.carryOverIds[file.id];
        this.applyBatchLimit();
      });

      // Добавим новые события для отслеживания этапов
      this.uppy.on('file-added', (file) => {
        console.log('File added:', file.name, file.size);
      });

      this.uppy.on('upload-progress', (file, progress) => {
        const progressPercentage = Math.floor((progress.bytesUploaded / progress.bytesTotal) * 100);
        console.log(`Upload progress: ${file.name} - ${progressPercentage}%`);
      });

      // Отслеживание этапов компрессии, если включен Compressor
      this.uppy.on('preprocess-progress', (file, progress) => {
        console.log(`Compression progress for ${file.name}: ${Math.round(progress * 100)}%`);
      });

      this.uppy.on('preprocess-complete', (file) => {
        console.log(`Compression complete for ${file.name}`);
        if (file.meta.compressorOriginalSize) {
          const ratio = Math.round((1 - file.size / file.meta.compressorOriginalSize) * 100);
          console.log(`Reduced by ${ratio}%`);
        }
      });

      this.uppy.on('upload', () => {
        console.log('Upload started');
      });

      this.uppy.on('complete', (result) => {
        console.log('Upload complete:', result);

        // FT-152: сначала СОБИРАЕМ карточки для галереи, и только потом снимаем файлы.
        //
        // Порядок здесь не косметика. `confirm_upload` отвечает `202 {status:"processing"}`
        // и ссылки НЕ отдаёт — настоящая появится позже, когда отработает
        // ProcessBookingPhotoJob. Поэтому галерея показывает локальный превью, и он обязан
        // пережить уборку: ThumbnailGenerator на `file-removed` делает
        // `revokeObjectURL(file.preview)` (@uppy/thumbnail-generator/lib/index.js:147-149).
        // Снятие файлов раньше сбора превращало ВСЕ только что загруженные фотографии в
        // серые заглушки — до перезагрузки страницы. Найдено ручной приёмкой 12.09.
        //
        // С момента, как карточка берёт номер строки из МЕТЫ файла, порядок значим и сам
        // по себе: снятый файл теряет мету вместе с собой. Раньше перестановка была
        // только вопросом читаемости — теперь она ломает идентификаторы, и это закреплено
        // спекой.
        //
        // Прежняя ветка `uploadedPhotos` отсюда убрана: её наполнение закомментировано в
        // коде с 2025 года, список всегда был пуст, и настоящим путём давно работал сбор
        // ниже. Живой `if` над мёртвым списком ровно этим и обманывает читателя.
        const gallery = result.successful.map((file) => {
          let responseData = {};
          try {
            if (file.response && file.response.body) {
              responseData = typeof file.response.body === 'string'
                ? JSON.parse(file.response.body)
                : file.response.body;
            }
          } catch (e) {
            console.error('Error parsing response:', e);
            console.log('Response body:', file.response ? file.response.body : 'no response body');
          }

          // keepPreview РАНЬШЕ uploadURL: под multipart uploadURL это presigned GET на
          // 900 секунд (uppy-s3_multipart/client.rb), и карточка протухала бы через
          // четверть часа, обходя фикс FT-152 против серых заглушек.
          const preview = responseData.url || this.keepPreview(file) || file.uploadURL;

          return {
            // Номер строки booking_photos, а НЕ идентификатор Uppy.
            //
            // Галерея бьёт этим идентификатором по пяти серверным адресам (полный размер,
            // скачивание, удаление, две сортировки), поэтому у свежезагруженной фотографии
            // все пять кнопок молча не работали до перезагрузки страницы, пока сюда уходил
            // file.id вида `uppy-scratch/jpg-...`.
            //
            // Настоящий номер приходит из подтверждения и лежит в мете файла. Ветки
            // `responseData.*` оставлены как запасные, но под multipart они не про наш
            // API: `file.response` здесь — тело `completeMultipartUpload`, которое гем
            // отдаёт как `{ location }` (uppy-s3_multipart/app.rb), номера строки там нет.
            id: responseData.id || file.meta.booking_photo_id || file.id,
            url: preview,
            small_url: responseData.small_url || preview,
            position: responseData.position || 0,
            content_type: file.type,
            checkin: true
          };
        });

        // FT-152 REQ-03: лимит считается ЗА ОТПРАВКУ, а не накопительно за сессию.
        // Доставленные снимаем (они уже собраны в gallery выше), упавшие оставляем —
        // им нужна штатная кнопка повтора. Но Restricter считает ВСЕ файлы в состоянии,
        // поэтому одного удаления мало: после пачки сплошных провалов следующая приняла бы
        // ноль, то есть жалоба «система сама выбирает» воспроизвелась бы снова.
        result.successful.forEach((f) => this.uppy.removeFile(f.id));
        this.carryOverIds = {};
        this.uppy.getFiles().forEach((f) => { this.carryOverIds[f.id] = true; });
        this.applyBatchLimit();

        if (gallery.length > 0) {
          this.$emit('updateCheckinPhotos', gallery);
        } else if (result.failed && result.failed.length > 0) {
          console.warn('Some files not uploaded', result.failed);
        }
      });

      // FT-152: подтверждение — ПОСТПРОЦЕССОР, а не обработчик после успеха.
      //
      // Раньше подтверждение уходило отдельным fetch уже ПОСЛЕ того, как Uppy объявил
      // файл загруженным: галочка зеленела, пачка уходила в галерею, а провал приезжал
      // вдогонку. Теперь исход подтверждения попадает в «готово» вместе со всеми.
      //
      // Ошибку файлу отдаём СОБЫТИЕМ, а не броском: на брошенном исключении Uppy 2.3.1
      // снимает загрузку целиком (Uppy.js:1732-1736), файлу error не ставит, «готово» не
      // эмитит — файл висит в спиннере, а кнопка «повторить всё» появляется мёртвой,
      // потому что retryAll фильтрует по file.error. Плюс бросок обрывает остаток пачки.
      this.uppy.addPostProcessor(async (fileIDs) => {
        // Core отдаёт исходный список — в том числе упавший файл, потому что
        // keepBatchAlive вернул агрегации прежнюю семантику (см. utils/uppyS3Multipart).
        // Подтверждать упавший нельзя: объекта в бакете нет.
        const ready = fileIDs.filter((id) => {
          const f = this.uppy.getFile(id);
          return f && !f.error && f.progress && f.progress.uploadComplete;
        });

        // FT-153 REQ-09: подтверждения идут ОГРАНИЧЕННОЙ параллельностью, а не все разом.
        // Каждое синхронно ждёт HEAD в треде api, а слотов на весь прод двенадцать.
        //
        // Ограничитель ОДИН на компонент (создан в instantiateUppy), а не на вызов: при
        // allowMultipleUploads вторая пачка входит сюда, пока первая ещё подтверждается,
        // и пул на вызов дал бы шесть одновременных запросов вместо трёх.
        //
        // Изоляция задач — здесь же, явным catch на каждый файл: общий Promise.all по
        // пачке означал бы, что одна снятая посреди паузы фотография оставляет остальные
        // неподтверждёнными.
        if (!this.confirmAbort) this.confirmAbort = new AbortController();
        const signal = this.confirmAbort.signal;

        await Promise.all(ready.map((id) => this.confirmLimiter.run(async () => {
          const file = this.uppy.getFile(id);
          if (!file) return;

          const key = file.s3Multipart && file.s3Multipart.key;

          try {
            if (!key) throw new Error('missing multipart key');

            // FT-153 REQ-07: повторяем ПОДТВЕРЖДЕНИЕ, а не загрузку. Байты уже в бакете;
            // пересылать файл заново — ровно то, против чего фича и существует.
            const res = await confirmWithRetries(() => fetch(
              `${this.$store.getters.getApiUrl}/api/v1/booking_photos/${this.booking_id}/confirm_multipart`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': this.$store.getters.getAuthToken
                },
                body: JSON.stringify({
                  key,
                  filename: file.name,
                  content_type: file.type,
                  checkin: true
                }),
                // Сигнал уходит и В САМ запрос, а не только в паузы между попытками:
                // иначе уничтожение компонента прекращало повторы, но до трёх уже
                // отправленных подтверждений продолжали держать треды api.
                signal
              }
            ), {
              signal,
              // Проверяется перед КАЖДОЙ попыткой: файл могли снять во время паузы, и
              // тогда повтор завёл бы строку, которой в интерфейсе нет.
              shouldContinue: () => Boolean(this.uppy.getFile(id))
            });

            const data = await res.json();
            // Номер строки живёт В САМОМ ФАЙЛЕ, а не во внешней карте: id файла в Uppy
            // детерминирован, и внешняя карта переживала снятие файла — на этом FT-152
            // уже поймала регрессию.
            this.uppy.setFileMeta(file.id, { booking_photo_id: data.booking_photo_id });
          } catch (e) {
            // Сюда попадаем, только когда повторы подтверждения исчерпаны или отказ
            // детерминирован. Файл могли снять, пока шли паузы, — тогда трогать его
            // состояние нельзя: setFileState на снятом бросает.
            if (!this.uppy.getFile(id)) return;

            // Сброс — ПОСЛЕДНЕЕ средство: если подтвердить объект не выходит в принципе,
            // перезалив остаётся единственным путём, и штатная кнопка его запустит.
            this.uppy.setFileState(id, { s3Multipart: null });
            this.uppy.emit('upload-error', file, new Error(this.$t('bookings.photo_confirm_failed')));
          }
        }).catch(() => {
          // Изоляция пачки: что бы ни бросила задача сверх собственного catch (например
          // setFileState на файле, снятом ровно между проверкой и вызовом), остальные
          // файлы обязаны подтвердиться.
        })));
      });

      // Добавляем обработчик ошибок ограничения для лучшей обратной связи
      this.uppy.on('restriction-failed', (file, error) => {
        let message = '';

        switch(error.reason) {
          case 'minNumberOfFiles':
            message = 'Please select at least one file';
            break;
          case 'maxNumberOfFiles':
            message = 'Too many files selected';
            break;
          case 'allowedFileTypes':
            message = `File type not allowed: ${file.name}. Allowed types: images, videos, HEIC/HEIF`;
            break;
          default:
            message = error.message || 'File rejected';
        }

        // FT-152 REQ-03: отказ по ограничению больше не немой. Uppy 2.3.1 глотает
        // restriction-ошибки в addFiles, а информер живёт 5 секунд — сотрудник узнавал,
        // что часть фотографий отброшена, только по их отсутствию.
        console.warn(message);
        // Список отклонённых относится к ОДНОМУ выбору. addFiles валидирует всю пачку
        // синхронно (Uppy.js: restriction-failed эмитится в цикле, file-added — после
        // него), поэтому первый отказ пачки обнуляет список, а микротаска закрывает
        // раунд уже после возврата из addFiles. Без этого список копится за сессию и
        // через две отправки называет файлы, которых сотрудник уже не помнит.
        if (!this.rejectionRoundOpen) {
          this.rejectedFiles = [];
          this.rejectionRoundOpen = true;
          Promise.resolve().then(() => { this.rejectionRoundOpen = false; });
        }
        this.rejectedFiles = [...this.rejectedFiles, file && file.name].filter(Boolean);
      });
    },
    // Превью для галереи, переживающее уборку Uppy: ссылку, созданную библиотекой,
    // она же и отзовёт на `file-removed`. Поэтому берём данные файла и делаем свою.
    keepPreview(file) {
      if (!file.data || typeof URL.createObjectURL !== 'function') return file.preview;
      const url = URL.createObjectURL(file.data);
      this.ownedPreviews.push(url);
      return url;
    },
    // FT-152 REQ-03: потолок = BATCH_LIMIT новых файлов СВЕРХ оставшихся упавших.
    // Restricter считает все файлы в состоянии, поэтому без слагаемого carryOver пачка
    // с 15 провалами обнулила бы следующую отправку — ровно жалоба клиента.
    applyBatchLimit() {
      if (!this.uppy) return;
      this.uppy.setOptions({
        restrictions: {
          ...this.uppy.opts.restrictions,
          maxNumberOfFiles: BATCH_LIMIT + Object.keys(this.carryOverIds).length,
        },
      });
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
  },
};
</script>

<style scoped>
.image-container {
  height: 150px;
  width: 150px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: auto;
  margin-left: auto;
}

.image-container>img {
  width: inherit;
  height: inherit;
}
</style>
